import { isStoreLevel, orbitRoleLabel, usesOrbitRoles } from "@/lib/roles";
import { DemoTour } from "@/components/DemoTour";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { OrbitLogo, Breadcrumb, UniversalSearch, CreateMenu, NotificationsBell, UserMenu, OrbitIAButton, TenantSwitcher } from "@/components/topbar/Topbar";
import { useAuth } from "@/lib/auth";
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import {
  LayoutDashboard, ClipboardList, AlertTriangle, Target,
  Newspaper, LineChart, Bell, ShieldCheck, LogOut, Search, Menu, X,
  Store, Calendar, FileText, User, Command, ChevronDown, ChevronRight,
  TrendingUp, PiggyBank, Users as UsersIcon, ShoppingBag, Activity, ExternalLink, Sliders,
  Database, MapPin, Wrench, LifeBuoy, Megaphone,
  Building2, ScanLine, Thermometer, SprayCan, Truck, ClipboardCheck, History, BarChart3,
  Home as HomeIcon, Boxes, Briefcase, BookOpen, FileBarChart2, GraduationCap } from "lucide-react";
import AiChatBubble from "@/components/AiChatBubble";
import { SidebarRail, SidebarDrawer } from "@/components/Sidebar";
import ErrorBoundary from "@/components/ErrorBoundary";
import { PushPromptBanner } from "@/components/PushNotifications";
import { DataLoadBar } from "@/components/DataLoadBar";

function GlobalSearch({ defaultAi = false }) {
  const [q, setQ] = useState("");
  const [aiMode, setAiMode] = useState(defaultAi);
  const [results, setResults] = useState(null);
  const [aiResults, setAiResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const boxRef = useRef(null);

  useEffect(() => {
    if (aiMode) return;
    if (!q || q.length < 2) { setResults(null); return; }
    const t = setTimeout(() => {
      api.get("/search", { params: { q } }).then(r => { setResults(r.data); setOpen(true); }).catch(() => setResults(null));
    }, 250);
    return () => clearTimeout(t);
  }, [q, aiMode]);

  const runAi = async () => {
    if (!q || q.length < 3) return;
    setLoading(true); setAiResults(null); setOpen(true);
    try { const { data } = await api.get("/search/ai", { params: { q } }); setAiResults(data); }
    catch { setAiResults({ explanation: "Erreur", corners: [] }); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const nav = (path) => { setOpen(false); setQ(""); navigate(path); };
  const total = results ? (results.stores.length + results.missions.length + results.plans.length + results.issues.length) : 0;

  return (
    <div className="relative" ref={boxRef}>
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
      <input data-testid="topbar-search" value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => q && setOpen(true)}
        onKeyDown={(e) => { if (aiMode && e.key === "Enter") runAi(); }}
        placeholder={aiMode ? "Ex : « corners à surveiller »" : "Rechercher magasin, mission, plan…"}
        className="w-full pl-9 pr-24 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white text-sm outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"/>
      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
        <button data-testid="search-ai-toggle" onClick={() => { setAiMode(!aiMode); setResults(null); setAiResults(null); }}
                className={`text-[10px] font-bold px-2 py-1 rounded-md transition ${aiMode ? "bg-gradient-to-r from-purple-600 to-blue-600 text-white" : "bg-white border border-slate-200 text-slate-500 hover:text-slate-700"}`}>✨ IA</button>
      </div>
      {open && (
        <div data-testid="search-results" className="absolute z-40 top-full mt-2 w-full max-w-xl bg-white border border-slate-200 rounded-xl shadow-lg max-h-[70vh] overflow-auto scrollbar-thin">
          {aiMode ? (
            <div>
              {loading && <div className="px-3 py-4 text-sm text-slate-400 text-center">Analyse IA en cours…</div>}
              {!loading && !aiResults && <div className="px-3 py-4 text-sm text-slate-400 text-center">Tapez votre question et appuyez sur Entrée</div>}
              {aiResults && (
                <>
                  {aiResults.explanation && (
                    <div className="px-3 py-2.5 bg-gradient-to-r from-purple-50 to-blue-50 border-b border-slate-100 text-sm text-slate-700">
                      <span className="font-semibold">✨ </span>{aiResults.explanation}
                    </div>
                  )}
                  {(aiResults.corners || []).map((c) => (
                    <button key={c.corner} onClick={() => nav(`/kpi/scores`)} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-blue-50 text-sm text-left">
                      <span className={`inline-block w-9 text-center font-bold text-xs px-1.5 py-0.5 rounded ${c.score >= 80 ? "bg-emerald-100 text-emerald-700" : c.score >= 60 ? "bg-blue-100 text-blue-700" : c.score >= 40 ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>{c.score}</span>
                      <span className="font-semibold text-slate-900 flex-1">{c.corner}</span>
                      <span className="text-xs text-slate-500">{c.dr} · {c.bm}</span>
                    </button>
                  ))}
                </>
              )}
            </div>
          ) : results && (
            <>
              {total === 0 && <div className="px-3 py-4 text-sm text-slate-400 text-center">Aucun résultat</div>}
              {["stores","missions","plans","issues"].map(cat => (results[cat]?.length > 0 && (
                <div key={cat}>
                  <div className="px-3 py-1.5 text-[10px] font-bold tracking-wider text-slate-400 uppercase bg-slate-50">{cat} ({results[cat].length})</div>
                  {results[cat].map(x => (
                    <button key={x.id} onClick={() => nav(cat === "stores" ? `/stores/${x.id}` : `/${cat}`)}
                            className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm">
                      <span className="font-semibold text-slate-900">{x.name || x.title || x.objective}</span>
                    </button>
                  ))}
                </div>
              )))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

const roleLabel = (r, u) => orbitRoleLabel(u) || ({ admin: "Administrateur", director: "Directeur régional", manager: "Responsable secteur", controller: "Contrôleur de gestion", rt: "Formateur (RT)", mm: "Manager Multi-sites (MM)", corner: "Magasin" }[r] || r);

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [aiOpen, setAiOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [perfScore, setPerfScore] = useState(null);
  const [lastNotifIds, setLastNotifIds] = useState(new Set());
  useEffect(() => {
    api.get("/kpi/summary").then(r => {
      const s = r.data;
      // Compute average health score from personal scope
      if (s?.avg_score != null) setPerfScore(Math.round(s.avg_score));
      else if (s?.perf_score != null) setPerfScore(Math.round(s.perf_score));
      else {
        // Fallback: fetch geo-perf and compute
        api.get("/geo-perf").then(g => {
          const arr = g.data || [];
          const withPerf = arr.filter(x => x.perf != null);
          if (withPerf.length) {
            const avg = withPerf.reduce((a, x) => a + (x.perf * 100), 0) / withPerf.length;
            setPerfScore(Math.round(avg));
          }
        }).catch(() => {});
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "default") {
      try { Notification.requestPermission(); } catch {}
    }
    const load = async () => {
      try {
        const { data } = await api.get("/notifications");
        setUnread(data.filter((n) => !n.read).length);
        if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted") {
          const newImportant = data.filter(n => !n.read && !lastNotifIds.has(n.id)
                                              && ["mission_assigned","score_alert","mission_suggestion"].includes(n.type));
          newImportant.slice(0, 3).forEach(n => {
            try {
              const notif = new Notification("Orbit — " + (n.type === "score_alert" ? "Alerte score" : n.type === "mission_assigned" ? "Nouvelle mission" : "Suggestion IA"),
                { body: n.message || n.title, icon: "/favicon.ico", tag: n.id });
              notif.onclick = () => { window.focus(); navigate("/notifications"); };
            } catch {}
          });
        }
        setLastNotifIds(new Set(data.map(n => n.id)));
      } catch {}
    };
    load(); const t = setInterval(load, 20000); return () => clearInterval(t);
  }, [navigate]);

  const initials = (user?.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
  const todayLabel = new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });

  return (
    <div className="flex min-h-screen" style={{ background: "var(--bg-app)" }}>
      <SidebarRail user={user} unread={unread} perfScore={perfScore} initials={initials} roleLbl={roleLabel(user?.role, user)}/>

      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setDrawerOpen(false)}/>
          <aside className="absolute left-0 top-0 bottom-0 w-72 flex flex-col text-slate-200 safe-top" style={{ background: "linear-gradient(180deg, #0f1f3d 0%, #0b1830 100%)" }}>
            <button data-testid="drawer-close" onClick={() => setDrawerOpen(false)} className="absolute right-3 z-10 w-9 h-9 grid place-items-center rounded-lg bg-white/10 text-white hover:bg-white/20" style={{ top: "calc(0.75rem + env(safe-area-inset-top, 0px))" }}><X className="w-5 h-5"/></button>
            <SidebarDrawer user={user} unread={unread} perfScore={perfScore} initials={initials} roleLbl={roleLabel(user?.role, user)} onNav={() => setDrawerOpen(false)}/>
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 sm:h-[72px] sticky top-0 z-40 bg-white border-b border-slate-200 flex items-center px-3 sm:px-5 gap-2 sm:gap-3 safe-top" data-testid="topbar">
          <button data-testid="mobile-menu-btn" onClick={() => setDrawerOpen(true)} className="lg:hidden w-10 h-10 grid place-items-center rounded-lg hover:bg-slate-100 text-slate-600"><Menu className="w-5 h-5"/></button>
          <OrbitLogo/>
          <div className="hidden lg:block w-px h-8 bg-slate-200 mx-1"/>
          <Breadcrumb/>
          <UniversalSearch/>
          <TenantSwitcher/>
          <CreateMenu/>
          <OrbitIAButton onClick={() => setAiOpen(true)}/>
          <NotificationsBell unread={unread} onRead={() => setUnread(u => Math.max(0, u - 1))}/>
          <div className="hidden sm:block w-px h-8 bg-slate-200 mx-1"/>
          <UserMenu unread={unread} roleLabel={roleLabel(user?.role, user)}/>
        </header>
        {aiOpen && <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-start justify-center pt-24 p-4" onMouseDown={e => e.target === e.currentTarget && setAiOpen(false)}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl p-4" data-testid="topbar-ia-panel"><div className="flex items-center justify-between mb-3"><h3 className="font-bold text-[#0b1b3a] flex items-center gap-2">✨ Orbit IA</h3><button onClick={() => setAiOpen(false)} className="p-1 rounded-md hover:bg-slate-100"><X className="w-4 h-4"/></button></div><GlobalSearch defaultAi/></div>
        </div>}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 pb-24 sm:pb-24 lg:pb-24">
          <ErrorBoundary name="page"><DataLoadBar/><PushPromptBanner/><DemoTour/><Outlet /></ErrorBoundary>
        </main>
      </div>
      <AiChatBubble />
    </div>
  );
}
